import React from "react";
import { QREditorContext } from "@/contexts/QREditorContext";
import { QREditorContextProps, QRProps } from "@/contexts/QREditorContext/types";
import { QRType } from "@/types/qr";

export interface UsePreviewDataOptions {
  prioritizeContext?: boolean;
  /** Callback when a change in context is detected */
  onContextChange?: (hasContext: boolean) => void;
}

/**
 * Hook to standardize data resolution between props vs QREditorContext
 * Safely handles context when preview is rendered in standalone templates
 */
export function usePreviewData<T extends QRType>(
  props: QRProps<T>,
  options: UsePreviewDataOptions = {}
) {
  const { prioritizeContext = false, onContextChange } = options;
  const context = React.useContext<QREditorContextProps<T>>(QREditorContext);

  // Context Safety: verify existence before using the context
  const hasContext = Boolean(context && context.qrData);

  const data = React.useMemo(() => {
    // Resolve data according to configured priority
    if (prioritizeContext && hasContext) {
      return context.qrData.data || props?.data;
    }
    // Default: props takes priority, fallback to context
    return props?.data || (hasContext ? context.qrData.data : null);
  }, [props?.data, hasContext, prioritizeContext, hasContext ? context.qrData.data : null]);

  // Callback for context changes
  React.useEffect(() => {
    onContextChange?.(hasContext);
  }, [hasContext, onContextChange]);

  const isInitPreview = React.useMemo(() => {
    // isInitPreview can come from props explicitly or be inferred from context
    return props?.initPreview ?? false;
  }, [props?.initPreview]);

  return {
    data,
    isInitPreview,
    context: hasContext ? context : null,
    hasContext,
  };
}
